import { ESettingsPaths } from "scenes/Settings/Settings.types";
import {
  EAcademyChapter,
  EAcademyMedia,
  IAcademyChapter,
  IAcademySlide,
  IAcademyStartStep,
  TAcademyQuizOption,
} from "./RentmatchAcademy.types";

// The router mounts the academy under this segment; every link is built from
// ACADEMY_BASE_PATH, so the two cannot drift apart.
export const ACADEMY_ROUTE = "rentmatch-academy";

export const ACADEMY_BASE_PATH = `/${ACADEMY_ROUTE}`;

export const ACADEMY_SHORT_PATH = "short";

export const ACADEMY_I18N_PREFIX = "rentmatch_academy";

// A search result opens its chapter at one section (or the short version at
// one slide) with the query in the address, so the match can be highlighted
// there and the back button returns to the same results.
export const ACADEMY_SEARCH_PARAM = "q";

export const ACADEMY_SECTION_PARAM = "section";

export const ACADEMY_SLIDE_PARAM = "slide";

export const ACADEMY_SEARCH_MIN_LENGTH = 2;

// Characters of context on either side of the match in a result's snippet.
export const ACADEMY_SEARCH_SNIPPET_CONTEXT = 60;

export const ACADEMY_QUIZ_OPTIONS: TAcademyQuizOption[] = [1, 2, 3];

// The order is the order a broker meets the features in, from publishing a
// property to choosing a tenant. Only what is live on main belongs here.
export const ACADEMY_CHAPTERS: IAcademyChapter[] = [
  {
    id: EAcademyChapter.Publish,
    cover: EAcademyMedia.RentmatchSettingsForm,
    sections: [
      { id: "activate", quizAnswer: 2 },
      {
        id: "publish",
        media: EAcademyMedia.RentmatchSettingsForm,
        quizAnswer: 1,
      },
      { id: "crm", quizAnswer: 3 },
      { id: "extra", media: EAcademyMedia.ExtraTab, quizAnswer: 2 },
      {
        id: "card",
        media: EAcademyMedia.PropertyCandidatesCard,
        quizAnswer: 1,
      },
      { id: "badge", media: EAcademyMedia.PropertyListBadge, quizAnswer: 2 },
    ],
  },
  {
    id: EAcademyChapter.Tenant,
    cover: EAcademyMedia.MatchCalculator,
    sections: [
      { id: "property_page", media: EAcademyMedia.PropertyPage, quizAnswer: 1 },
      { id: "calculator", media: EAcademyMedia.MatchCalculator, quizAnswer: 2 },
      { id: "tips", media: EAcademyMedia.ContinueModal, quizAnswer: 2 },
      { id: "requests", media: EAcademyMedia.MyApplications, quizAnswer: 1 },
    ],
  },
  {
    id: EAcademyChapter.MatchingProperties,
    cover: EAcademyMedia.MatchingProperties,
    sections: [
      { id: "feed", media: EAcademyMedia.MatchingProperties, quizAnswer: 2 },
      { id: "detail", media: EAcademyMedia.MatchingDetail, quizAnswer: 2 },
      { id: "alerts", media: EAcademyMedia.MatchingAlerts, quizAnswer: 2 },
      { id: "network", quizAnswer: 1 },
    ],
  },
  {
    id: EAcademyChapter.Overview,
    cover: EAcademyMedia.CandidateList,
    sections: [
      { id: "steps", media: EAcademyMedia.CandidateTabs, quizAnswer: 3 },
      { id: "score", media: EAcademyMedia.ScoreZoom, quizAnswer: 2 },
      { id: "table", media: EAcademyMedia.CandidateList, quizAnswer: 2 },
      { id: "filters", media: EAcademyMedia.FilterPanel, quizAnswer: 1 },
      { id: "new", quizAnswer: 2 },
      { id: "drawer", media: EAcademyMedia.CandidateDrawer, quizAnswer: 1 },
      { id: "other", media: EAcademyMedia.OtherApplications, quizAnswer: 3 },
    ],
  },
  {
    id: EAcademyChapter.Pipeline,
    cover: EAcademyMedia.StepMailEditor,
    sections: [
      { id: "move", media: EAcademyMedia.RowMenu, quizAnswer: 2 },
      { id: "mails", media: EAcademyMedia.StepMailEditor, quizAnswer: 1 },
      { id: "message", media: EAcademyMedia.BulkMessage, quizAnswer: 2 },
      { id: "invite", media: EAcademyMedia.InviteToApply, quizAnswer: 1 },
      { id: "row", quizAnswer: 2 },
      { id: "tenant", quizAnswer: 1 },
    ],
  },
  {
    id: EAcademyChapter.Waitlist,
    cover: EAcademyMedia.CandidateTabs,
    sections: [
      { id: "manual", quizAnswer: 2 },
      { id: "auto", media: EAcademyMedia.CandidateTabs, quizAnswer: 1 },
    ],
  },
  {
    id: EAcademyChapter.Visits,
    cover: EAcademyMedia.AvailabilityCalendar,
    sections: [
      { id: "calendar", media: EAcademyMedia.ProfileAgenda, quizAnswer: 2 },
      { id: "slots", media: EAcademyMedia.AvailabilityCalendar, quizAnswer: 3 },
      { id: "booking", media: EAcademyMedia.BookingSlots, quizAnswer: 1 },
      { id: "confirmations", quizAnswer: 2 },
    ],
  },
  {
    id: EAcademyChapter.AutoMails,
    cover: EAcademyMedia.InvitationMail,
    sections: [
      { id: "invite", media: EAcademyMedia.InvitationMail, quizAnswer: 2 },
      { id: "booking_reminder", quizAnswer: 2 },
      { id: "slot", quizAnswer: 1 },
      {
        id: "visit_reminder",
        media: EAcademyMedia.VisitReminderMail,
        quizAnswer: 3,
      },
      { id: "confirmation", quizAnswer: 1 },
      { id: "broker", quizAnswer: 2 },
    ],
  },
  {
    id: EAcademyChapter.VisitMode,
    cover: EAcademyMedia.VisitCard,
    sections: [
      { id: "day_mail", quizAnswer: 2 },
      { id: "open", media: EAcademyMedia.VisitMode, quizAnswer: 1 },
      { id: "rate", media: EAcademyMedia.VisitCard, quizAnswer: 3 },
      { id: "noshow", media: EAcademyMedia.NoShow, quizAnswer: 2 },
      { id: "invite", quizAnswer: 1 },
    ],
  },
  {
    id: EAcademyChapter.Owner,
    cover: EAcademyMedia.SendToOwner,
    sections: [
      { id: "select", media: EAcademyMedia.SendToOwner, quizAnswer: 2 },
      { id: "level", quizAnswer: 1 },
      { id: "link", quizAnswer: 3 },
    ],
  },
  {
    id: EAcademyChapter.Settings,
    cover: EAcademyMedia.ScoreSettings,
    sections: [
      { id: "score", media: EAcademyMedia.ScoreSettings, quizAnswer: 2 },
      { id: "per_property", quizAnswer: 1 },
      { id: "mails", media: EAcademyMedia.SettingsMails, quizAnswer: 3 },
      {
        id: "intake",
        media: EAcademyMedia.SettingsTenantProfile,
        quizAnswer: 1,
      },
    ],
  },
  {
    id: EAcademyChapter.Faq,
    cover: EAcademyMedia.PropertyPage,
    isQuestionList: true,
    sections: [
      { id: "score_factors", group: "matching" },
      { id: "own_criteria" },
      { id: "adjust_answers" },
      { id: "own_score" },
      { id: "other_properties" },
      { id: "discrimination" },
      { id: "data" },
      { id: "students" },
      { id: "not_digital" },
      { id: "documents" },
      { id: "noshow_other_properties", group: "noshow" },
      { id: "noshow_other_offices" },
      { id: "colleagues" },
      { id: "calendar_outlook", group: "visits" },
      { id: "crm_calendar" },
      { id: "slot_length" },
      { id: "no_slots_yet" },
      { id: "too_many" },
      { id: "invite_more" },
      { id: "reschedule" },
      { id: "without_calendar" },
      { id: "email_only", group: "communication" },
      { id: "languages" },
      { id: "unsubscribe" },
      { id: "portal_leads", group: "portals" },
      { id: "no_website" },
      { id: "website" },
      { id: "mhp" },
      { id: "online_after_tenant" },
    ],
  },
];

// What an office does before its first Rentmatch candidate (Jonas, DEV-3526).
// Visit moments are set per property when inviting, and the default mails
// already cover the new features, so neither is a step here. The settings to
// check are a step of their own, before publishing, so the first candidates
// already get the right mails and score (Jonas, DEV-3555).
export const ACADEMY_START_STEPS: IAcademyStartStep[] = [
  { id: "calendar", chapter: EAcademyChapter.Visits },
  {
    id: "settings",
    chapter: EAcademyChapter.Settings,
    links: [
      {
        id: "notifications",
        path: `/${ESettingsPaths.Base}/${ESettingsPaths.TenantProfileNotifications}`,
      },
      {
        id: "score",
        path: `/${ESettingsPaths.Base}/${ESettingsPaths.RentmatchScore}`,
      },
    ],
  },
  { id: "publish", chapter: EAcademyChapter.Publish },
];

// The FAQ's themes, in the order they appear; each opens at the section that
// carries its id as \`group\`.
export const ACADEMY_FAQ_GROUPS = [
  "matching",
  "noshow",
  "visits",
  "communication",
  "portals",
];

// The short version follows the final Rentio Connect presentation
// (rentio-connect-live/deck-source): its titles and visuals, with the
// explanation rewritten to stand without a speaker (Jonas, DEV-3526).
export const ACADEMY_SLIDES: IAcademySlide[] = [
  { id: "intro", media: EAcademyMedia.SlideIntro },
  { id: "steps" },
  { id: "same_link", media: EAcademyMedia.SlideSameLink },
  { id: "activate", media: EAcademyMedia.SlideActivate },
  { id: "match", media: EAcademyMedia.MatchCalculator },
  { id: "tips", media: EAcademyMedia.SlideTips },
  { id: "rules", media: EAcademyMedia.ScoreSettings },
  { id: "other_properties", media: EAcademyMedia.MatchingProperties },
  { id: "candidate_tabs", media: EAcademyMedia.SlideCandidateTabs },
  { id: "auto_mails", media: EAcademyMedia.InvitationMail },
  { id: "mail_editor", media: EAcademyMedia.SlideMailEditor },
  { id: "calendar", media: EAcademyMedia.SlideCalendar },
  { id: "agenda", media: EAcademyMedia.SlideAgenda },
  { id: "booking", media: EAcademyMedia.SlideBooking },
  { id: "visit_mode", media: EAcademyMedia.VisitMode },
  { id: "select", media: EAcademyMedia.CandidateList },
  { id: "elsewhere", media: EAcademyMedia.SlideOtherProperties },
  { id: "filters", media: EAcademyMedia.SlideFilters },
  { id: "owner", media: EAcademyMedia.SlideOwner },
  { id: "start" },
];
